import { Link } from 'react-router-dom';
import { useAuth } from '../../context/useAuth';
import { isFeatureEnabled } from '../../config/features';

interface Feature {
  title: string;
  description: string;
  comingSoon?: boolean;
}

interface HeroFeature {
  heading: string;
  bullets: [string, string];
}

const heroFeatures: HeroFeature[] = [
  {
    heading: 'Compare Withdrawal Strategies — See the Tax Impact',
    bullets: [
      'Compare SIPP-first, ISA-first, pro-rata, and tax-optimised strategies side by side',
      'Guardrail spending auto-adjusts when withdrawal rates run too high',
    ],
  },
  {
    heading: 'What-If Scenario Analysis',
    bullets: [
      'Retire earlier, spend more, or sell property — see the year-by-year impact instantly',
      'Seven scenario types across timing, spending, returns, windfalls, gifting, and property',
    ],
  },
  {
    heading: 'Joint Plans for Couples',
    bullets: [
      'Separate pensions, tax positions, and retirement dates — modelled together',
      'See what happens when one partner retires years before the other',
    ],
  },
  {
    heading: 'Historical Market Replay',
    bullets: [
      '100 years of US market data — from the Great Depression to the GFC (UK data coming soon)',
      'Rolling-window analysis shows best, worst, and median outcomes for every start year',
    ],
  },
];

const moreFeatures: Feature[] = [
  { title: 'Alternative Plans & Comparison', description: 'Clone your plan, tweak the assumptions, and compare both projections side by side.' },
  { title: 'Spending Approaches', description: 'Choose from constant percentage, Vanguard dynamic, or probability-based guardrail spending strategies.' },
  { title: 'Inheritance Tax Planning & Gifting', description: 'Track estate value year by year with nil-rate bands, RNRB, and six gifting strategies.' },
  { title: 'Randomised Market Scenarios', description: 'See how your projection holds up across thousands of randomised market scenarios.' },
  { title: 'Property Downsizing', description: 'Model sale proceeds, SDLT, mortgage payoff, and reinvestment into your portfolio.' },
  { title: 'Market Shock Scenarios', description: 'Model GFC-style crashes with V, U, and L-shaped recoveries.' },
  { title: 'Spending Sensitivity', description: 'See how spending changes affect your plan\'s longevity.' },
  { title: 'Multiple Spending Phases', description: 'Set different spending levels for early, mid, and late retirement — because your 60s won\'t look like your 80s.' },
  { title: 'Time-Horizon Rebalancing', description: 'Per-wrapper allocation that shifts from growth to defensive as your spending date approaches.' },
  { title: 'State Pension Deferral', description: 'Model the impact of deferring your State Pension.' },
  { title: 'Supplementary Income', description: 'Add rental income, part-time work, or other income streams.' },
  { title: 'Care Cost Modelling', description: 'Plan for later-life care expenses and their portfolio impact.' },
];

const annuityFeature = { title: 'Annuity purchase and hybrid drawdown', description: 'Swap part of the pension for guaranteed income for life and keep the rest in drawdown.' };
// Listed as shipped only in a build that ships it; otherwise it stays a promise.
if (isFeatureEnabled('annuityPurchase')) moreFeatures.push(annuityFeature);

const comingSoon: Feature[] = [
  ...(isFeatureEnabled('annuityPurchase') ? [] : [{ ...annuityFeature, comingSoon: true }]),
  { title: 'Survivor Report', description: 'A report showing the surviving partner\'s financial position.', comingSoon: true },
  { title: 'Am I On Track', description: 'Compare your current portfolio against the original plan.', comingSoon: true },
  { title: 'Tax Efficiency Score', description: 'A single score quantifying how tax-efficient your plan is.', comingSoon: true },
];

export function FeaturesSection() {
  const { isAuthenticated } = useAuth();

  return (
    <section className="landing-section landing-features" id="features">
      <div className="landing-section-inner">
        <div className="landing-section-header">
          <h2 className="landing-section-title">See exactly how much tax you could pay — for every strategy, every year</h2>
          {isAuthenticated && (
            <Link to="/tools" className="landing-section-tools-link">See all tools &rarr;</Link>
          )}
        </div>

        <div className="landing-hero-features">
          {heroFeatures.map((hf) => (
            <div key={hf.heading} className="landing-hero-feature">
              <h3>{hf.heading}</h3>
              <ul className="landing-hero-feature-bullets">
                {hf.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <details className="landing-features-disclosure" open>
          <summary className="landing-features-category-heading landing-features-toggle">
            Also included — {moreFeatures.length} more features
          </summary>
          <div className="landing-features-grid">
            {moreFeatures.map((f) => (
              <div key={f.title} className="landing-feature-card">
                <h4>{f.title}</h4>
                <p>{f.description}</p>
              </div>
            ))}
          </div>
        </details>

        <h3 className="landing-features-category-heading">Coming soon</h3>
        <div className="landing-features-grid">
          {comingSoon.map((f) => (
            <div key={f.title} className="landing-feature-card landing-feature-card--coming-soon">
              <div className="landing-feature-card-header">
                <h4>{f.title}</h4>
                <span className="landing-badge-coming-soon">Coming soon</span>
              </div>
              <p>{f.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
